import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import React, { useContext, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SelectedLocaleContext } from "../context";

const langFontOptions = {
  fontFamily: "azo-sans-web",
  fontStyle: "normal"
};

const useStyles = makeStyles({
  button: {
    textTransform: "none",
    whiteSpace: "nowrap",
    color: "#fff",

    "& .MuiButton-label": {
      display: "flex",
      gap: 5
    }
  },
  langCode: {
    padding: "2px 4px",
    backgroundColor: "#259678",
    borderRadius: 4,
    textAlign: "center",
    fontWeight: 700,
    letterSpacing: "0.04px",
    ...langFontOptions
  },
  langLabel: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 700,
    letterSpacing: "0.04px",
    ...langFontOptions
  },
  menuPaper: {
    "& ol, & ul": { padding: 8 },
    "& ol li, & ul li": {
      borderRadius: 4,
      minWidth: "110px",

      padding: "8px 12px",
      "& p": {
        lineHeight: "20px",
        fontWeight: 400,

        ...langFontOptions
      },
      transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
    },
    "& .MuiMenuItem-root:hover": { backgroundColor: "#259678", color: "#fff" },
    "& .MuiMenuItem-root.Mui-selected": {
      backgroundColor: "#259678",
      color: "#fff"
    },
    "& .MuiMenuItem-root.Mui-selected:hover": {
      backgroundColor: "#259678",
      color: "#fff"
    }
  }
});

interface Language {
  code: string;
  name: string;
  id?: string | number;
}

interface GlobalSettings {
  language_label?: string;
  locale?: Array<{ languages_code: string; language_label?: string }>;
}

interface LanguageSelectorProps {
  languages: Language[];
  globalSettings?: GlobalSettings;
}

export default function LanguageSelector({
  languages,
  globalSettings
}: LanguageSelectorProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { selectedLocale, setLocale } = useContext(SelectedLocaleContext) as {
    selectedLocale: string | { code: string; id?: string | number } | null;
    setLocale: (code: string) => void;
  };
  const history = useHistory();
  const location = useLocation();

  const currentCode =
    typeof selectedLocale === "string"
      ? selectedLocale
      : (selectedLocale?.code ?? languages?.[0]?.code ?? "");

  const displayCode = currentCode.slice(0, 2).toUpperCase();

  const localeEntry = globalSettings?.locale?.find(
    (l) => l.languages_code?.toLowerCase() === currentCode.toLowerCase()
  );
  const languageLabel =
    localeEntry?.language_label || globalSettings?.language_label || "Language";

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (code: string) => {
    setLocale(code);
    const params = new URLSearchParams(location.search);
    params.set("lang", code);
    history.replace({ search: params.toString(), hash: location.hash });
    handleClose();
  };

  return (
    <>
      <Button className={classes.button} onClick={handleOpen}>
        <Typography
          variant="body2"
          component="span"
          className={classes.langLabel}
        >
          {languageLabel}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.langCode}
        >
          {displayCode}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.menuPaper }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code.toLowerCase() === currentCode.toLowerCase()}
            onClick={() => handleSelect(lang.code)}
          >
            <Typography variant="body2">{lang.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
